import { z } from 'zod';

/**
 * The closed outcome the host-owned booking controller resolves with when the
 * member finishes with the `book_care_team_call` card — `booked` on confirm,
 * `cancelled` on an explicit dismiss, `not_booked` when the card's timeout
 * elapses. The client tool remains pending while the member interacts with the
 * card, and the validated outcome is returned to the agent after any required
 * persistence completes. All three statuses are graceful tool completions;
 * malformed data or an operational failure rejects instead of fabricating one.
 */
export const careTeamBookingOutcomeSchema = z.discriminatedUnion('status', [
  z
    .object({
      status: z.literal('booked'),
      date: z.string().min(1),
      time: z.string().min(1),
      provider: z.string().min(1),
      // Machine instant for the selected slot (ISO-8601). Carried alongside the
      // human `date`/`time` labels so the host can persist the appointment and
      // the home screen can re-format it, while the agent speaks the labels.
      scheduledFor: z.string().min(1),
    })
    .strict(),
  z.object({ status: z.literal('cancelled') }).strict(),
  z
    .object({
      status: z.literal('not_booked'),
      reason: z.enum(['unconfirmed']).optional(),
    })
    .strict(),
]);

export type CareTeamBookingOutcome = z.infer<typeof careTeamBookingOutcomeSchema>;
